import React, { useState, useEffect, useRef } from 'react';
import { db } from '../../../config/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import './AccommodationHistory.css';

interface AccommodationRecord {
  id: string;
  year: number;
  inventoryId: string;
  inventoryName: string;
}

interface AccommodationHistoryProps {
  primaryCamperId: string;
  isReadOnly?: boolean;
}

const AccommodationHistory: React.FC<AccommodationHistoryProps> = ({ primaryCamperId, isReadOnly = false }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [history, setHistory] = useState<AccommodationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editedValues, setEditedValues] = useState<{[key: string]: string}>({});
  const [editingHistory, setEditingHistory] = useState<AccommodationRecord[]>([]);
  const nextHistoryRowId = useRef(0);

  const createHistoryRowId = () => `history_${nextHistoryRowId.current++}`;
  const getHistoryValueKey = (record: AccommodationRecord) => `history_${record.id}`;

  const isNoHistoryValue = (value: string): boolean => {
    const normalized = String(value || '').trim().toLowerCase();
    return normalized === '' || normalized === 'did not attend' || normalized === 'no history';
  };

  const sortHistoryDescendingWithValues = (
    records: AccommodationRecord[],
    values: {[key: string]: string}
  ) => {
    const combined = records.map((record) => ({
      record,
      value: values[getHistoryValueKey(record)] !== undefined
        ? values[getHistoryValueKey(record)]
        : record.inventoryId
    }));

    combined.sort((a, b) => Number(b.record.year || 0) - Number(a.record.year || 0));

    const sortedHistory = combined.map((item) => item.record);
    const sortedValues: {[key: string]: string} = {};
    combined.forEach((item) => {
      sortedValues[getHistoryValueKey(item.record)] = item.value || '';
    });

    return { sortedHistory, sortedValues };
  };

  useEffect(() => {
    const loadAccommodationHistory = async () => {
      if (!currentUser) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('Loading accommodation history for camper:', primaryCamperId);

        // Get the primary camper document
        const camperRef = doc(db, 'primaryCampers', primaryCamperId);
        const camperDoc = await getDoc(camperRef);
        
        if (!camperDoc.exists()) {
          throw new Error('Camper document not found');
        }

        const camperData = camperDoc.data();
        console.log('Camper data:', camperData);

        // Read from the new accommodationHistory array (indices 0-7 for 8 years)
        const accommodationHistory = camperData.accommodationHistory || [];
        console.log('Accommodation history array:', accommodationHistory);

        // Map the array to our display format
        const historyArray: AccommodationRecord[] = accommodationHistory.map((record: any) => ({
          id: createHistoryRowId(),
          year: record.year || 0,
          inventoryId: isNoHistoryValue(record.placeCode) ? '' : (record.placeCode || ''),
          inventoryName: isNoHistoryValue(record.placeCode) ? 'Did Not Attend' : (record.placeCode || 'Did Not Attend')
        }));
        historyArray.sort((a, b) => Number(b.year || 0) - Number(a.year || 0));

        console.log('Final history array:', historyArray);
        setHistory(historyArray);
      } catch (err) {
        console.error('Detailed error in loading accommodation history:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to load accommodation history';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    if (primaryCamperId) {
      loadAccommodationHistory();
    }
  }, [primaryCamperId, currentUser]);

  const handleEditClick = () => {
    const initialValues: {[key: string]: string} = {};
    history.forEach((record) => {
      initialValues[getHistoryValueKey(record)] = record.inventoryId;
    });
    setEditedValues(initialValues);
    setEditingHistory([...history]);
    setError('');
    setIsEditing(true);
  };

  const handleAddYear = () => {
    const sourceHistory = editingHistory;
    if (sourceHistory.length >= 8) {
      setError('Maximum of 8 years can be stored in accommodation history.');
      return;
    }

    const numericYears = sourceHistory
      .map((record) => Number(record.year))
      .filter((year) => Number.isFinite(year) && year > 0);
    const nextYear = numericYears.length > 0
      ? Math.min(...numericYears) - 1
      : new Date().getFullYear();

    const newRecord: AccommodationRecord = {
      id: createHistoryRowId(),
      year: nextYear,
      inventoryId: '',
      inventoryName: 'Did Not Attend'
    };
    const nextHistory = [
      ...sourceHistory,
      newRecord
    ];
    const nextValues = {
      ...editedValues,
      [getHistoryValueKey(newRecord)]: ''
    };
    const { sortedHistory, sortedValues } = sortHistoryDescendingWithValues(nextHistory, nextValues);
    setEditingHistory(sortedHistory);
    setEditedValues(sortedValues);
    setError('');
  };

  const handleYearChange = (index: number, yearValue: string) => {
    const parsedYear = Number(yearValue);
    const nextHistory = editingHistory.map((record, i) =>
        i === index
          ? {
              ...record,
              year: Number.isFinite(parsedYear) ? parsedYear : 0
            }
          : record
    );
    const { sortedHistory, sortedValues } = sortHistoryDescendingWithValues(nextHistory, editedValues);
    setEditingHistory(sortedHistory);
    setEditedValues(sortedValues);
  };

  const handleRemoveYear = (indexToRemove: number) => {
    const nextHistory = editingHistory.filter((_, index) => index !== indexToRemove);
    const { sortedHistory, sortedValues } = sortHistoryDescendingWithValues(nextHistory, editedValues);
    setEditingHistory(sortedHistory);
    setEditedValues(sortedValues);
    setError('');
  };

  const validatePlaceCode = async (placeCode: string) => {
    console.log('Validating place code:', placeCode);
    if (isNoHistoryValue(placeCode)) return true;
    
    try {
      const inventoryRef = doc(db, 'inventory', 'accommodations');
      const inventoryDoc = await getDoc(inventoryRef);
      
      if (!inventoryDoc.exists()) {
        console.log('Inventory document does not exist');
        return false;
      }
      
      const data = inventoryDoc.data();
      console.log('Checking inventory data:', data);

      // Convert input to lowercase for case-insensitive comparison
      const searchCode = placeCode.toLowerCase();

      // List of all accommodation types to check
      const accommodationTypes = [
        'Apartment',
        'Cottage',
        'Fireside',
        'LowerCourt',
        'UpperCourt',
        'dormA',
        'dormB',
        'privateCottage'
      ];

      // Check each accommodation type
      for (const type of accommodationTypes) {
        if (Array.isArray(data[type])) {
          // Look through each item in the array
          for (const unit of data[type]) {
            if (unit?.placeCode?.toLowerCase() === searchCode) {
              console.log(`Found matching placeCode in ${type}:`, unit.placeCode);
              return true;
            }
          }
        }
      }
      
      console.log('No matching placeCode found');
      return false;
    } catch (error) {
      console.error('Error validating place code:', error);
      return false;
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
      // Validate all place codes
      const validationPromises = Object.entries(editedValues).map(async ([key, value]) => {
        const isValid = await validatePlaceCode(value);
        if (!isValid) {
          throw new Error(`Invalid place code: ${value}`);
        }
      });
      
      await Promise.all(validationPromises);

      // Validate years
      const invalidYear = editingHistory.find((record) => !Number.isFinite(Number(record.year)) || Number(record.year) <= 0);
      if (invalidYear) {
        throw new Error('All history rows must have a valid year.');
      }
      const normalizedYears = editingHistory.map((record) => Number(record.year));
      const uniqueYears = new Set(normalizedYears);
      if (uniqueYears.size !== normalizedYears.length) {
        throw new Error('Each history row must have a unique year.');
      }

      // Build the new accommodationHistory array
      const newAccommodationHistory = editingHistory.map((record) => {
        const key = getHistoryValueKey(record);
        const placeCode = editedValues[key] !== undefined ? editedValues[key] : record.inventoryId;
        
        return {
          year: record.year,
          placeCode: isNoHistoryValue(placeCode) ? '' : placeCode
        };
      });
      newAccommodationHistory.sort((a, b) => Number(b.year || 0) - Number(a.year || 0));

      console.log('Saving new accommodation history:', newAccommodationHistory);
      
      // Save to Firestore
      const camperRef = doc(db, 'primaryCampers', primaryCamperId);
      await updateDoc(camperRef, {
        accommodationHistory: newAccommodationHistory
      });
      
      console.log('Document updated successfully');
      
      // Refresh the history
      window.location.reload();
    } catch (err) {
      console.error('Error saving changes:', err);
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setLoading(false);
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedValues({});
    setEditingHistory([]);
    setError('');
  };

  if (loading) {
    return <div className="loading">Loading history...</div>;
  }

  return (
    <div className="accommodation-history">
      <h2>Accommodation History</h2>
      {error ? (
        <div className="error-message">
          {error}
          <p className="error-help">Please try refreshing the page. If the problem persists, contact support.</p>
        </div>
      ) : (
        <>
          <div className="history-list">
            {(isEditing ? editingHistory : history).map((record, index) => (
              <div key={record.id} className="history-item">
                {isEditing ? (
                  <input
                    type="number"
                    value={record.year || ''}
                    onChange={(e) => handleYearChange(index, e.target.value)}
                    className="year"
                    style={{ width: '90px' }}
                    min={1900}
                    max={2500}
                  />
                ) : (
                  <span className="year">{record.year}</span>
                )}
                {isEditing ? (
                  <>
                    <input
                      type="text"
                      value={editedValues[getHistoryValueKey(record)] || ''}
                      onChange={(e) => {
                        setEditedValues({
                          ...editedValues,
                          [getHistoryValueKey(record)]: e.target.value
                        });
                      }}
                      className="accommodation-input"
                      placeholder="Enter place code"
                    />
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleRemoveYear(index)}
                      style={{ marginLeft: '0.5rem' }}
                    >
                      Remove
                    </Button>
                  </>
                ) : record.inventoryName === 'Did Not Attend' ? (
                  <span className="accommodation" title="Did Not Attend">{record.inventoryName}</span>
                ) : (
                  <a 
                    href="#"
                    className="accommodation"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/admin/inventory?placeCode=${record.inventoryId}&select=true`);
                    }}
                  >
                    {record.inventoryName}
                  </a>
                )}
              </div>
            ))}
          </div>
          {!isReadOnly && (
            <div className="action-buttons mt-3">
              {isEditing ? (
                <>
                  <Button variant="primary" onClick={handleSave} className="me-2">
                    Save Changes
                  </Button>
                  <Button variant="outline-primary" onClick={handleAddYear} className="me-2">
                    Add Year
                  </Button>
                  <Button variant="secondary" onClick={handleCancel}>
                    Cancel
                  </Button>
                </>
              ) : (
                <Button variant="primary" onClick={handleEditClick}>
                  Edit History
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AccommodationHistory; 